import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import Caption from 'LuxKit/Typography/Caption'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import config from 'constants/config'
import { MALDIVES_BUNDLE_DEFAULT_NIGHTS } from 'constants/charterFlight'
import useCharterBundlePricing from 'hooks/CharterFlights/useCharterBundlePricing'
import type { CharterBundlePricingInput } from 'hooks/CharterFlights/useCharterBundlePricing'

interface Props {
  pricingToDisplay: App.PricingWithValue
  memberPricingToDisplay?: App.PricingWithValue
  totalGuests: number
  isPricePerPerson: boolean
  pkg: App.HotelPackage
  offer: App.Offer
  shouldShowValue: boolean
  showDiscountPill: boolean
  guestsDisplayText?: string
  shouldShowRoomType: boolean
  isUsingFallbackPricing: boolean
  pointsEarnCalculationRequests: Array<App.LoyaltyPointsEarnCalculationRequest | undefined>
  onViewPackageOptions: () => void
  calendarsLoading?: boolean
}

function mapOfferPricingToInput(
  pricingToDisplay: App.PricingWithValue,
  memberPricingToDisplay: App.PricingWithValue | undefined,
  showDiscountPill: boolean,
  totalGuests: number,
): CharterBundlePricingInput {
  return {
    price: pricingToDisplay.price,
    memberPrice: memberPricingToDisplay?.price,
    value: pricingToDisplay.value,
    discountPercent: showDiscountPill ? pricingToDisplay.discountPercent : undefined,
    totalGuests,
  }
}

function FlashOfferCharterSummary({
  pricingToDisplay,
  memberPricingToDisplay,
  totalGuests,
  isPricePerPerson,
  pkg,
  offer,
  shouldShowValue,
  showDiscountPill,
  guestsDisplayText,
  shouldShowRoomType,
  isUsingFallbackPricing,
  pointsEarnCalculationRequests,
  onViewPackageOptions,
  calendarsLoading,
}: Props) {
  const intl = useIntl()

  const input = useMemo(
    () => mapOfferPricingToInput(pricingToDisplay, memberPricingToDisplay, showDiscountPill, totalGuests),
    [pricingToDisplay, memberPricingToDisplay, showDiscountPill, totalGuests],
  )
  const {
    displayPrice,
    memberDisplayPrice,
    valuePrice,
    discountPercentage,
    showMemberPricing,
  } = useCharterBundlePricing(input, isPricePerPerson)

  const viewOptionsLabel = intl.formatMessage(
    {
      id: 'JaczHbGlU3Scp_uyLG7u',
      defaultMessage: 'View {travelSynonym} options',
      description: 'Action button label to view travel options on the offer detail page',
    },
    { travelSynonym: config.brandConfig.travelSynonym },
  )

  const nights = pkg.duration < MALDIVES_BUNDLE_DEFAULT_NIGHTS ? MALDIVES_BUNDLE_DEFAULT_NIGHTS : pkg.duration

  const nightsAndFlightsLabel = isUsingFallbackPricing
    ? intl.formatMessage(
        {
          id: 'MvPReD3cazr5vAzqCSxM',
          defaultMessage: '{nights} nights & flights from',
          description: 'Text showing nights and flights with from prefix for fallback pricing',
        },
        { nights },
      )
    : intl.formatMessage(
        {
          id: '4GssUqxdJT-Ju8SQ3FFn',
          defaultMessage: '{nights} nights & flights',
          description: 'Text showing nights and flights included in charter package',
        },
        { nights },
      )

  return (
    <div>
      <VerticalSpacer gap={16}>
        <Group direction="vertical" horizontalAlign="start" gap={8}>
          <Group direction="vertical" horizontalAlign="start">
            <PriceRowPriceCaption startIcon={<LinePlaneDepartureIcon />}>
              {nightsAndFlightsLabel}
            </PriceRowPriceCaption>
            <LuxPlusPriceStack
              size="L"
              price={displayPrice ?? 0}
              memberPrice={showMemberPricing ? memberDisplayPrice : undefined}
              saleUnit={isPricePerPerson ? 'person' : 'total'}
              testid="pricing"
              horizontalAlign="start"
              offerConfig={offer.luxPlus}
            />
            {shouldShowValue && valuePrice !== undefined && <PriceRowValueDiscountWithCaption
              data-testid="value-box"
              size="L"
              originalValue={valuePrice}
              discountPercentage={discountPercentage}
            />}
            {guestsDisplayText && <PriceRowCaption>{guestsDisplayText}</PriceRowCaption>}
            {shouldShowRoomType && (
              <Caption variant="medium" colour="light-foreground-alt">
                {pkg.roomType?.name}
              </Caption>
            )}
          </Group>
          <LoyaltyPointsEarn
            horizontalAlign="center"
            calculationRequests={pointsEarnCalculationRequests}
            calculationType="estimate"
            offer={offer}
          />
        </Group>
        <TextButton
          kind="primary"
          data-testid="OfferAvailable"
          onClick={onViewPackageOptions}
          fit="flex"
        >
          {viewOptionsLabel}
        </TextButton>
      </VerticalSpacer>
      <LoadingIndicator floating backdrop="grey-light" opaque visible={!!calendarsLoading} />
    </div>
  )
}

export default FlashOfferCharterSummary
